import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, LOGO_URL, getJoinRef } from "@/lib/api";
import { ReferrerBanner } from "@/components/ReferrerBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Smartphone, User, Lock, ArrowLeft } from "lucide-react";
import { PageBackground } from "@/components/PageBackground";
import { Talbiyah, GLASS, SHELL } from "@/components/Talbiyah";

export default function AgentAccess() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const refCode = getJoinRef() || localStorage.getItem("member_ref") || "";
  React.useEffect(() => { if (refCode) localStorage.setItem("member_ref", refCode); }, [refCode]);
  const [signup, setSignup] = useState({ name: "", phone: "", password: "" });
  const [signin, setSignin] = useState({ phone: "", password: "" });
  const [cowork, setCowork] = useState({ name: "", phone: "" });
  const [reset, setReset] = useState(null);
  const [loading, setLoading] = useState(false);

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/agent/forgot-password", { phone: reset.phone });
      toast.success(data.message || "OTP sent on WhatsApp");
      if (data.dev_otp) toast.info(`Simulation OTP: ${data.dev_otp}`, { duration: 15000 });
      setReset({ ...reset, step: 2 });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/agent/reset-password", { phone: reset.phone, otp: reset.otp, new_password: reset.password });
      toast.success(data.message || "Password updated — please sign in");
      setReset(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doCoworker = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/coworker/login", cowork);
      applyAuth(data);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/commission");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/agent/signup", { ...signup, ref: refCode });
      localStorage.removeItem("member_ref");
      localStorage.removeItem("join_ref");
      applyAuth(data);
      toast.success(`Welcome, ${data.user.name}! Your associate partner account is ready.`);
      navigate("/commission");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doSignin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/agent/login", signin);
      applyAuth(data);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate("/commission");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={SHELL}>
      <PageBackground dim={0.04} />
      <Talbiyah />
      <div className={`w-full max-w-[380px] fade-in ${GLASS}`}>
        <Link to="/login" data-testid="agent-back-link" className="inline-flex items-center gap-1.5 text-xs text-emerald-100/60 hover:text-gold mb-5 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to company login
        </Link>
        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO_URL} alt="Bengal Umrah House" className="w-24 h-24 rounded-full object-cover ring-2 ring-gold/70 shadow-[0_0_45px_rgba(212,175,55,0.4)]" />
          <h1 className="font-serif text-2xl font-semibold text-white mt-4">Associate Partner & Member Login</h1>
          <p className="eyebrow text-gold">Bengal Umrah House Team</p>
        </div>

        {reset ? (
          <form onSubmit={reset.step === 1 ? doForgot : doReset} className="space-y-4" data-testid="agent-reset-form">
            <p className="text-sm text-gold font-serif font-semibold text-center">Reset Password via WhatsApp OTP</p>
            <div>
              <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Registered mobile number</Label>
              <Input data-testid="agent-reset-phone" value={reset.phone} onChange={(e) => setReset({ ...reset, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required disabled={reset.step === 2} />
            </div>
            {reset.step === 2 && (
              <>
                <div>
                  <Label className="text-xs text-emerald-100/70">OTP from WhatsApp</Label>
                  <Input data-testid="agent-reset-otp" value={reset.otp} onChange={(e) => setReset({ ...reset, otp: e.target.value })} placeholder="6-digit OTP" className="mt-1.5 bg-[#05261D]/70 border-gold/30 font-mono tracking-widest" required />
                </div>
                <div>
                  <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> New password</Label>
                  <Input data-testid="agent-reset-newpass" type="password" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} placeholder="Min. 6 characters" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
                </div>
              </>
            )}
            <Button type="submit" data-testid="agent-reset-submit" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : reset.step === 1 ? "Send OTP on WhatsApp" : "Reset Password"}
            </Button>
            <button type="button" data-testid="agent-reset-cancel" onClick={() => setReset(null)} className="w-full text-[11px] text-emerald-100/50 hover:text-gold transition-colors">Back to sign in</button>
          </form>
        ) : (
        <Tabs defaultValue="signin">
          <TabsList className="grid grid-cols-3 w-full bg-card/50 border border-gold/25 mb-4">
            <TabsTrigger value="signin" data-testid="agent-tab-signin" className="text-xs">Partner Login</TabsTrigger>
            <TabsTrigger value="signup" data-testid="agent-tab-signup" className="text-xs">Sign Up</TabsTrigger>
            <TabsTrigger value="coworker" data-testid="agent-tab-coworker" className="text-xs">Name + Mobile</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <form onSubmit={doSignin} className="space-y-4" data-testid="agent-signin-form">
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Mobile number</Label>
                <Input data-testid="agent-signin-phone" value={signin.phone} onChange={(e) => setSignin({ ...signin, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Password</Label>
                <Input data-testid="agent-signin-password" type="password" value={signin.password} onChange={(e) => setSignin({ ...signin, password: e.target.value })} placeholder="••••••••" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <Button type="submit" data-testid="agent-signin-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
              </Button>
              <button type="button" data-testid="agent-forgot-link" onClick={() => setReset({ step: 1, phone: signin.phone, otp: "", password: "" })} className="w-full text-[11px] text-emerald-100/50 hover:text-gold transition-colors text-center">Forgot password? Get OTP on WhatsApp</button>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={doSignup} className="space-y-4" data-testid="agent-signup-form">
              <ReferrerBanner code={refCode} />
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Full name</Label>
                <Input data-testid="agent-signup-name" value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} placeholder="e.g. Imran Hossain" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Mobile number</Label>
                <Input data-testid="agent-signup-phone" value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Create a password</Label>
                <Input data-testid="agent-signup-password" type="password" value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} placeholder="Min. 6 characters" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <Button type="submit" data-testid="agent-signup-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Associate Partner Account"}
              </Button>
              <p className="text-[11px] text-emerald-100/50 text-center">You'll join Bengal Umrah House and can start managing your own customers right away.</p>
            </form>
          </TabsContent>

          <TabsContent value="coworker">
            <form onSubmit={doCoworker} className="space-y-4" data-testid="coworker-login-form">
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Your name</Label>
                <Input data-testid="coworker-name" value={cowork.name} onChange={(e) => setCowork({ ...cowork, name: e.target.value })} placeholder="As given by your Associate Partner" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <div>
                <Label className="text-xs text-emerald-100/70 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Mobile number</Label>
                <Input data-testid="coworker-phone" value={cowork.phone} onChange={(e) => setCowork({ ...cowork, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1.5 bg-[#05261D]/70 border-gold/30" required />
              </div>
              <Button type="submit" data-testid="coworker-login-btn" disabled={loading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In with Name"}
              </Button>
              <p className="text-[11px] text-emerald-100/50 text-center">For partners added by the company or their upline with name + mobile. No password needed.</p>
            </form>
          </TabsContent>
        </Tabs>
        )}
      </div>
    </div>
  );
}
